import { watchImmediate } from '@vueuse/core';
import { CanvasTexture, LinearFilter, LinearMipMapLinearFilter, RepeatWrapping } from 'three';
import { stopAll, type StopHandle } from '@/util';
import { markRaw, type Raw } from 'vue';

/**
 * Reactive state for a [canvas-texture]{@link https://threejs.org/docs/#CanvasTexture},
 * which creates a texture from a
 * [canvas-element]{@link https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/canvas}.
 */
export type CanvasTextureState = {
    /** The source canvas for the texture */
    source: HTMLCanvasElement | null;

    /**
     * A version number for the source. The actual value does not matter: changing it
     * forces an update of the texture when the source canvas has changed.
     */
    sourceVersion: number;

    /**
     * The result canvas-texture.
     */
    texture: Raw<CanvasTexture> | null;
};

/**
 * Create the reactive state for a canvas-texture
 */
export function canvasTextureState(properties?: Partial<CanvasTextureState>): CanvasTextureState {
    return {
        source: properties?.source ?? null,
        sourceVersion: properties?.sourceVersion ?? 0,
        texture: null,
    };
}

/**
 * Update the reactive state for a canvas-texture
 */
export function updateCanvasTexture(state: CanvasTextureState): StopHandle {
    const disposeTexture = () => {
        if (state.texture !== null) {
            state.texture.dispose();
            state.texture = null;
        }
    };

    return stopAll(
        watchImmediate(
            () => state.source,
            () => {
                disposeTexture();
                if (state.source === null) {
                    return;
                }
                const texture = new CanvasTexture(state.source);
                texture.magFilter = LinearFilter;
                texture.minFilter = LinearMipMapLinearFilter;
                texture.wrapS = texture.wrapT = RepeatWrapping;
                state.texture = markRaw(texture);
            },
        ),
        watchImmediate(
            () => state.sourceVersion,
            () => {
                if (state.texture !== null) {
                    state.texture.needsUpdate = true;
                }
            }
        ),
        disposeTexture,
    );
}
